import { isString, flow, camelCase, upperFirst, unescape } from 'lodash'

import { REGEXP_URL, REGEXP_EMAIL_GLOBAL } from 'src/utils/pattern/pattern'
import { escapeForMarkup, isSafeTarget } from 'src/utils/safe_url/safe_url'
import { addSchemaIfNotPresent } from 'src/utils/url/url'

// Used for parsing user created messages and generating links from it:
//
// The text is escaped FIRST and linkified afterwards. The other way round the
// escaping would eat the anchors this function just built. Everything the author
// wrote therefore arrives in the DOM as text, and the only markup in the result
// is the anchors below.
//
// Each target is checked before it becomes a link, and a target we cannot link
// leaves the text in place rather than the attribute: the reader still sees what
// was written, it just is not clickable.
//
// Measured: that check cannot fail here. addSchemaIfNotPresent puts http:// in
// front of every target that does not already start with http:// or https://, so
// whatever REGEXP_URL matches, the target carries an allowed scheme - a widened
// pattern would link javascript:... as http://javascript:..., not run it. The
// guard stays in case addSchemaIfNotPresent changes. What keeps such text unlinked
// is the pattern, which only ever matches something beginning with http://,
// https:// or www.; what checks that, for the schemes it lists, is the test
// "only ever matches targets we are allowed to link" in string_spec.
export const makeHTMLLinksFromText = (text: string): string => {
  if (!isString(text)) {
    return text
  }

  return escapeForMarkup(text)
    .replace(REGEXP_URL, (escapedUrl) => {
      // Check the value the author wrote, write out the escaped one - checking
      // the escaped form would make the same input behave differently here than
      // it does on the markdown path.
      const target = addSchemaIfNotPresent(unescape(escapedUrl))
      if (!isSafeTarget(target)) {
        return escapedUrl
      }

      return `<a href="${escapeForMarkup(target)}" target='_blank' rel='nofollow noopener'>${escapedUrl}</a>`
    })
    .replace(REGEXP_EMAIL_GLOBAL, (email) =>
      `<a href="mailto:${email}" target='_blank' rel='nofollow noopener'>${email}</a>`
    )
}

export const pascalCase = flow(camelCase, upperFirst)
